import React, { useState, useMemo, useEffect } from 'react';
import { CATEGORY_TREE, ALL_LEAF_CATEGORIES } from '../../constants/tags';
import { ChevronRight, Check } from 'lucide-react';

interface CategoryTreeSelectProps {
    primaryCategory: string | null;     // leaf category ID
    secondaryCategories: string[];       // up to 2 leaf category IDs
    onPrimaryChange: (categoryId: string | null) => void;
    onSecondaryChange: (categoryIds: string[]) => void;
    label?: string;
}

// Map leaf ID -> parent ID for quick lookup
const LEAF_TO_PARENT: Record<string, string> = {};
CATEGORY_TREE.forEach(parent => {
    parent.children?.forEach(leaf => {
        LEAF_TO_PARENT[leaf.id] = parent.id;
    });
});

export const CategoryTreeSelect: React.FC<CategoryTreeSelectProps> = ({
    primaryCategory,
    secondaryCategories,
    onPrimaryChange,
    onSecondaryChange,
    label = '业务分类'
}) => {
    // Default collapsed; auto-expand when a selection is made
    const [expandedParents, setExpandedParents] = useState<Set<string>>(
        () => new Set()
    );

    // Auto-expand parents that contain selected categories
    useEffect(() => {
        const toExpand = new Set<string>();
        if (primaryCategory) {
            const parentId = LEAF_TO_PARENT[primaryCategory];
            if (parentId) toExpand.add(parentId);
        }
        secondaryCategories.forEach(id => {
            const parentId = LEAF_TO_PARENT[id];
            if (parentId) toExpand.add(parentId);
        });
        if (toExpand.size > 0) {
            setExpandedParents(prev => {
                const next = new Set(prev);
                toExpand.forEach(id => next.add(id));
                return next;
            });
        }
    }, [primaryCategory, secondaryCategories]);

    const primaryNode = useMemo(() =>
        ALL_LEAF_CATEGORIES.find(c => c.id === primaryCategory),
        [primaryCategory]
    );

    const toggleParent = (parentId: string) => {
        setExpandedParents(prev => {
            const next = new Set(prev);
            if (next.has(parentId)) next.delete(parentId);
            else next.add(parentId);
            return next;
        });
    };

    const handleClickLeaf = (leafId: string) => {
        if (primaryCategory === leafId) {
            // Click primary -> deselect everything
            onPrimaryChange(null);
            onSecondaryChange([]);
        } else if (secondaryCategories.includes(leafId)) {
            // Click secondary -> deselect it
            onSecondaryChange(secondaryCategories.filter(id => id !== leafId));
        } else {
            // Click unselected
            if (!primaryCategory) {
                // No primary yet -> set as primary
                onPrimaryChange(leafId);
            } else if (secondaryCategories.length < 2) {
                // Has primary, room for secondary -> set as secondary
                onSecondaryChange([...secondaryCategories, leafId]);
            }
            // If secondary is full, do nothing (user must deselect one first)
        }
    };

    return (
        <div className="space-y-3">
            {label && (
                <label className="text-sm font-medium text-slate-700 block">
                    {label}
                    <span className="text-red-500 ml-1">*</span>
                    <span className="text-slate-400 font-normal ml-2 text-xs">
                        主分类必选，次要方向可选 0~2 个
                    </span>
                </label>
            )}

            {/* Selected summary */}
            {(primaryCategory || secondaryCategories.length > 0) && (
                <div className="flex flex-wrap gap-2 mb-2">
                    {primaryNode && (
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-blue-600 text-white text-sm font-medium">
                            <span className="text-xs opacity-70">主</span>
                            {primaryNode.name}
                            <button
                                type="button"
                                onClick={() => {
                                    onPrimaryChange(null);
                                    onSecondaryChange([]);
                                }}
                                className="ml-1 hover:text-blue-100"
                            >
                                ×
                            </button>
                        </span>
                    )}
                    {secondaryCategories.map(id => {
                        const node = ALL_LEAF_CATEGORIES.find(c => c.id === id);
                        if (!node) return null;
                        return (
                            <span
                                key={id}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-sm font-medium border border-blue-100"
                            >
                                <span className="text-xs opacity-60">次</span>
                                {node.name}
                                <button
                                    type="button"
                                    onClick={() => onSecondaryChange(secondaryCategories.filter(sid => sid !== id))}
                                    className="ml-1 hover:text-blue-900"
                                >
                                    ×
                                </button>
                            </span>
                        );
                    })}
                </div>
            )}

            {/* Tree */}
            <div className="space-y-1">
                {CATEGORY_TREE.map(parent => {
                    const isExpanded = expandedParents.has(parent.id);
                    const hasChildren = parent.children && parent.children.length > 0;

                    return (
                        <div key={parent.id} className="border border-slate-100 rounded-lg overflow-hidden">
                            {/* Parent header */}
                            <button
                                type="button"
                                onClick={() => hasChildren && toggleParent(parent.id)}
                                className="w-full flex items-center gap-2 px-3 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                            >
                                {hasChildren && (
                                    <ChevronRight
                                        className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                    />
                                )}
                                <span className="text-sm font-medium text-slate-700">{parent.name}</span>
                            </button>

                            {/* Children */}
                            {hasChildren && isExpanded && (
                                <div className="px-3 py-3 bg-white">
                                    <div className="flex flex-wrap gap-2">
                                        {parent.children!.map(leaf => {
                                            const isPrimary = primaryCategory === leaf.id;
                                            const isSecondary = secondaryCategories.includes(leaf.id);

                                            return (
                                                <button
                                                    key={leaf.id}
                                                    type="button"
                                                    onClick={() => handleClickLeaf(leaf.id)}
                                                    className={`
                                                        px-3 py-1.5 rounded-full text-sm font-medium transition-all border
                                                        ${isPrimary
                                                            ? 'bg-blue-600 text-white border-blue-600'
                                                            : isSecondary
                                                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                                                                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                                        }
                                                    `}
                                                    title={isPrimary ? '点击取消选择' : isSecondary ? '点击取消次要' : primaryCategory ? '点击设为次要方向' : '点击设为主分类'}
                                                >
                                                    {isPrimary && <Check className="w-3 h-3 inline mr-1" />}
                                                    {leaf.name}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
